/**
 * 작성자 : 홍선영
 * 날짜 : 2024.10.28
 * 기능 : 사원관리-사원정보
 */

import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { IComCdList, INumbers, ITabObject, ISelectBox } from 'customTypes';
import { COMCD_APPROVAL_YN, COMCD_LOCK_YN, COMCD_USE_YN, USER_GRANT, PMS_NORMAL_SUB_CODE, INIT_LOCK_YN_N, INIT_USE_YN_Y, INIT_WORKER_STATUS, WORK_STATUS_SEARCH2 } from '../../_constants';
import { pageInfoState, userState } from '../../atoms';
import UserInfo from './user1/userInfo';
import UserMenu from './user1/userMenu';
import { BtnGhost } from '../../components/Button';
import Tab from '../../components/Tab';
import Input from '../../components/Input';
import Portal from '../../components/Portal';
import SearchFilterModal from '../../components/Modal/SearchFilterModal';
import TuiGrid from '../../components/Table/TuiGrid';
import SelectBox from '../../components/SelectBox';
import BackButton from '../../components/BackButton';
import TuiGridWrapper from '../../components/Table/TuiGridWrapper';
import { apiGet } from '../../services/_common';
import { logPost } from '../../services/log';
import i18n from '../../translation/i18n';
import { ynFilter } from '../../utils/ynFilter';
import { setComCdListState } from '../../utils/setComCdListState';
import { useGetFrozenCount } from '../../utils/getFrozenCount';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import { TuiGridTwoColumnStyle } from '../../assets/styles/TuiGridTwoColumnStyle';
import illustrator from '../../assets/images/illustration/61.svg';
import useTuiGridSize from '../../hooks/useTuiGridSize';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import IssueGuide from '../../components/IssueGuide';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { useDebounce } from '../../utils/useDebounce';
import useBackHistory from '../../hooks/useBackHistory';
import { isMobile } from '../../utils/checkMobile';

const Root = styled(ContentsContainerRoot)`
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .tui-container {
    flex-grow: 1;
    flex-shrink: 1;
    overflow: hidden;
  }
  .tableWrapper {
    flex-grow: 1;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }
  input[type='checkbox'] {
    margin: 0;
    width: 1rem;
    height: 1rem;
  }
  .inputFormsWrapper {
    border-bottom: 1px solid var(--outline);
    padding: 0.5rem;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
    .table {
      height: fit-content;
    }
  }
  .inputFormsWrapper.smallTab {
    flex-direction: row;
    align-items: center;
    padding: 0 0.5rem;
    font-weight: 500;
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      height: 0rem !important;
    }
    > div {
      user-select: none;
    }
    .tab {
      /* width: fit-content; */
      color: var(--text-secondary);
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
      cursor: pointer;
      height: 3.5rem;
      border-bottom: 2px solid transparent;
    }
    .activeTab {
      font-weight: 700;
      color: var(--selected-primary);
      border-bottom: 2px solid var(--selected-primary);
    }
  }
  .formTitle {
    padding: 0 0.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    font-weight: 500;
    color: var(--text-primary);
  }
  .end {
    justify-content: end;
    > button {
      margin-left: 1rem;
    }
  }

  .thead > .tr,
  .tbody > .tr {
    > div {
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
    }
    .spaceBetween {
      justify-content: space-evenly;
    }
  }
  .grid {
    display: grid;
    margin-bottom: 1rem;
    align-items: center;
    min-height: 3.4rem;
  }

  .grid100,
  .grid100T,
  .grid60,
  .grid50,
  .grid30 {
    > div > div {
      display: grid;
      grid-template-columns: 9rem 1fr;
      align-items: center;
    }
  }

  .grid100T {
    grid-template-columns: 9rem 1fr;
  }

  .grid50,
  .grid60 {
    grid-template-columns: 1fr 1fr;
  }

  .grid50 {
    grid-gap: 1rem;
  }

  .grid30 {
    grid-template-columns: 1.5fr 0.7fr 0.8fr;
  }

  .plainText {
    grid-template-columns: 9rem 1fr;

    > :last-child {
      display: block;
    }
  }

  .marginBottomNone {
    margin-bottom: 0;
  }
`;
interface IViewTableObject {
  userId: string;
  userName: string;
  gName: string;
  lockYn: string;
  approvalYn: string;
  useYn: string;
}
type UpdatedLogoState = 'N' | 'D' | 'U';

const TAB_INFO = 0;
const TAB_MENU = 1;

const getWorkStatusName = (status: 'I' | 'R' | 'D' | 'T') => {
  return WORK_STATUS_SEARCH2.find((el) => el.wWorkstatus === status)?.cdName || '';
};

// 목록 순서를 서버 응답 순서에 의존하지 않고 항상 사원명 ㄱㄴㄷ 순으로 고정
const sortByUserName = (array: any[]) => [...array].sort((a: any, b: any) => (a.userName || '').localeCompare(b.userName || '', 'ko'));

const CUser1 = () => {
  const { data: userList, isLoading, isError, refetch } = GetFetchStatus({ path: '/user/company', hCdOnly: { value: true, key: 'hCd' } });
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const { userMenuList } = userInfo;
  const [isNewAdd, setIsNewAdd] = useState(true); // 신규등록 여부
  const [columns, setColumns] = useState<any[]>([]);
  const size = useOutletContext<any>();
  const tabInfo = useRecoilValue(pageInfoState);
  const [activeTabClick, setActiveTabClick] = useState(0);
  const [viewTableState, setViewTableState] = useState<any[]>([]);
  const [initTableState, setInitTableState] = useState<any[] | null>(null);
  const [viewTable, setViewTable] = useState<boolean>(true);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const [useYnComCdList, setUseYnComCdList] = useState<IComCdList[]>([]); // 사용유무 공통코드
  const [lockYnComCdList, setLockYnComCdList] = useState<IComCdList[]>([]); // 잠금유무 공통코드
  const [useYnComCdList2, setUseYnComCdList2] = useState<IComCdList[]>([]); // 사용유무 공통코드(내부 컴포넌트 용)
  const [lockYnComCdList2, setLockYnComCdList2] = useState<IComCdList[]>([]); // 잠금유무 공통코드(내부 컴포넌트 용)
  const [userGrantComCdList, setUserGrantComCdList] = useState<IComCdList[]>([]); // 사용자 권한 공통코드
  const [approvalYnComCdList, setApprovalYnComCdList] = useState<IComCdList[]>([]); // 사용유무 공통코드
  const [partList, setPartList] = useState<ISelectBox[]>([]); // 부서명 메뉴 리스트
  const [positionList, setPositionList] = useState<ISelectBox[]>([]); // 직급명 메뉴 리스트
  const [orgUserInfo, setOrgUserInfo] = useState(null);
  const [telNumState, setTelNumState] = useState<INumbers>({ num1: '', num2: '', num3: '' });
  const [hpNumState, setHpNumState] = useState<INumbers>({ num1: '010', num2: '', num3: '' });
  const [emerNumState, setEmerNumState] = useState<INumbers>({ num1: '', num2: '', num3: '' });
  const [ipInfoState, setIpInfoState] = useState<INumbers>({ num1: '', num2: '', num3: '', num4: '' });
  const [searchOption, setSearchOption] = useState({ userName: '' });
  const [searchOptionWorkStatus, setSearchOptionWorkStatus] = useState(INIT_WORKER_STATUS);
  const [searchOptionUseYn, setSearchOptionUseYn] = useState<any>({ type: COMCD_USE_YN, [COMCD_USE_YN]: 'A', cdName: t('전체'), cdSort: 0 });
  const [searchOptionLockYn, setSearchOptionLockYn] = useState<any>({ type: COMCD_LOCK_YN, [COMCD_LOCK_YN]: 'A', cdName: t('전체'), cdSort: 0 });
  const [searchOptionApprovalYn, setSearchOptionApprovalYn] = useState<any>({ type: COMCD_APPROVAL_YN, [COMCD_APPROVAL_YN]: 'A', cdName: t('전체'), cdSort: 0 });
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const [selectedRowKey, setSelectedRowKey] = useState<any>(null);
  const [lockYn, setLockYn] = useState(INIT_LOCK_YN_N); // 잠금유무 공통코드
  const [useYn, setUseYn] = useState(INIT_USE_YN_Y); // 사용유무 공통코드
  const [gCd, setGCd] = useState<{ type: 'USER_GRANT'; [USER_GRANT]: string; cdName: string }>({ type: USER_GRANT as 'USER_GRANT', [USER_GRANT]: '', cdName: '미선택' });
  const [partCd, setPartCd] = useState<ISelectBox>({ type: 'PART_CD', PART_CD: '', cdName: t('미선택') });
  const [positionCd, setPositionCd] = useState<ISelectBox>({ type: 'POSITION_CD', POSITION_CD: '', cdName: t('미선택') });
  const [wWorkstatus, setWWorkstatus] = useState<ISelectBox>(INIT_WORKER_STATUS);
  const [uJobdate, setUJobdate] = useState(todayYYYYMMDD);
  const [uBdate, setUBdate] = useState('');
  const [uRetiredate, setURetiredate] = useState('');
  const [userImage, setUserImage] = useState<string>('');
  const [logoType, setLogoType] = useState<any | null>();
  const [isUpdatedLogo, setIsUpdatedLogo] = useState<UpdatedLogoState>('N');
  const [openModal, setOpenModal] = useState({ status: false, type: '' });
  const debouncedUserName = useDebounce(searchOption.userName);
  const [searchParams] = useSearchParams();
  const tabCd = searchParams.get('tabCd');

  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const tabs = [
    { id: TAB_INFO, name: t('사원 정보') },
    { id: TAB_MENU, name: t('사원별 메뉴 관리') },
  ];

  // URL의 tabCd 파라미터 변경 감지하여 탭 업데이트
  useEffect(() => {
    if (tabCd && tabCd !== currentTabMrCd) {
      setCurrentTabMrCd(tabCd);
    }
  }, [tabCd]);

  useEffect(() => {
    if (userList) {
      const tuiForm = userList.map((el: any) => ({
        ...el,
        lockYnCdName: el.lockYn === 'Y' ? t('잠금') : t('미잠금'),
        approvalYnCdName: el.approvalYn === 'Y' ? t('승인') : t('미승인'),
        useYnCdName: el.useYn === 'Y' ? t('사용') : t('미사용'),
        uWorkstatusName: getWorkStatusName(el.uWorkstatus),
      }));
      setViewTableState(sortByUserName(tuiForm.filter((el: any) => el.uWorkstatus === searchOptionWorkStatus.wWorkstatus)));
      setInitTableState(tuiForm);
    }
  }, [userList]);

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    setSearchOptionLockYn({ type: COMCD_LOCK_YN, [COMCD_LOCK_YN]: 'A', cdName: t('전체'), cdSort: 0 });
    setSearchOptionApprovalYn({ type: COMCD_APPROVAL_YN, [COMCD_APPROVAL_YN]: 'A', cdName: t('전체'), cdSort: 0 });
    setSearchOptionUseYn({ type: COMCD_USE_YN, [COMCD_USE_YN]: 'A', cdName: t('전체'), cdSort: 0 });

    if (isMobile()) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const containerPadding = 0.75 * 2 * rootFontSize;
      const colWidth = Math.floor((document.body.clientWidth - containerPadding) / 4);
      setColumns([
        { header: t('ID'), name: 'userId', sortable: true, width: colWidth, renderer: { classNames: ['text_secondary'] } },
        { header: t('사원명'), name: 'userName', sortable: true, width: colWidth, renderer: { classNames: ['text_secondary'] } },
        { header: t('권한'), name: 'gName', align: 'center', sortable: true, width: colWidth },
        { header: t('근로 상태'), name: 'uWorkstatusName', align: 'center', sortable: true, width: colWidth },
      ]);
    } else {
      setColumns([
        {
          header: t('ID'),
          name: 'userId',
          sortable: true,
          width: 160,
          renderer: { classNames: ['text_secondary'] },
        },
        {
          header: t('사원명'),
          name: 'userName',
          sortable: true,
          minWidth: 100,
          renderer: { classNames: ['text_secondary'] },
        },
        {
          header: t('권한'),
          name: 'gName',
          align: 'center',
          sortable: true,
          filter: 'select',
          minWidth: 120,
        },
        {
          header: t('근로 상태'),
          name: 'uWorkstatusName',
          align: 'center',
          sortable: true,
          filter: 'select',
          minWidth: 120,
        },
        {
          header: t('잠금유무'),
          name: 'lockYnCdName',
          align: 'center',
          sortable: true,
          minWidth: 80,
        },
        {
          header: t('승인여부'),
          name: 'approvalYnCdName',
          align: 'center',
          sortable: true,
          minWidth: 80,
        },
        {
          header: t('사용유무'),
          name: 'useYnCdName',
          align: 'center',
          sortable: true,
          minWidth: 80,
        },
      ]);
    }
  }, [i18n.language]);

  useEffect(() => {
    setComCdListState(COMCD_APPROVAL_YN, setApprovalYnComCdList, true);
    setComCdListState(COMCD_USE_YN, setUseYnComCdList, true);
    setComCdListState(COMCD_LOCK_YN, setLockYnComCdList, true);
    setComCdListState(COMCD_USE_YN, setUseYnComCdList2, false);
    setComCdListState(COMCD_LOCK_YN, setLockYnComCdList2, false);
    setComCdListState(USER_GRANT, setUserGrantComCdList, false, 'etc1', 'C');

    setPmsNormalSubList('L', setPartList);
    setPmsNormalSubList('M', setPositionList);

    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '사원 정보 / 메뉴 관리',
      action: '조회',
      etc: ``,
    });
  }, []);

  useEffect(() => {
    if (initTableState) applyFilter(initTableState);
  }, [debouncedUserName, searchOptionWorkStatus.wWorkstatus, searchOptionUseYn[COMCD_USE_YN], searchOptionLockYn[COMCD_LOCK_YN], searchOptionApprovalYn[COMCD_APPROVAL_YN]]);

  useEffect(() => {
    if (isNewAdd) {
      setHpNumState({ num1: '010', num2: '', num3: '' });
      setTelNumState({ num1: '', num2: '', num3: '' });
      setIpInfoState({ num1: '', num2: '', num3: '', num4: '' });
      setEmerNumState({ num1: '', num2: '', num3: '' });
    }
  }, [isNewAdd]);

  const applyFilter = (array: any[]) => {
    // 검색옵션 변경됐을 때 필터링 처리
    // 필터링 기준
    const filterOptions = {
      userName: searchOption.userName,
    };
    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = array.filter((item: any) => {
      // 근로상태 필터
      const workStatusMatch = searchOptionWorkStatus.wWorkstatus === 'A' || item.uWorkstatus === searchOptionWorkStatus.wWorkstatus;
      // 사원명 필터
      const nameMatch = item.userName?.toLowerCase()?.includes(filterOptions.userName?.toLowerCase());
      return workStatusMatch && nameMatch;
    });

    const lockFilter = ynFilter(filteredArray, 'lockYn', searchOptionLockYn[COMCD_LOCK_YN]);
    const approvalFilter = ynFilter(lockFilter, 'approvalYn', searchOptionApprovalYn[COMCD_APPROVAL_YN]);
    const result = ynFilter(approvalFilter, 'useYn', searchOptionUseYn[COMCD_USE_YN]);
    if (result.length > 0) {
      const newTableState: Array<any> = [];
      for (const v of result) {
        newTableState.push({
          userId: v.userId,
          userName: v.userName,
          gName: v.gName,
          lockYn: v.lockYn,
          approvalYn: v.approvalYn,
          useYn: v.useYn,
          lockYnCdName: v.lockYn === 'Y' ? t('잠금') : t('미잠금'),
          approvalYnCdName: v.approvalYn === 'Y' ? t('승인') : t('미승인'),
          useYnCdName: v.useYn === 'Y' ? t('사용') : t('미사용'),
          uWorkstatusName: getWorkStatusName(v.uWorkstatus),
        });
      }
      setViewTableState(sortByUserName(newTableState));
    } else {
      setViewTableState([]);
    }
  };

  const subCodeFilter = (grCd: string) => {
    const codeFilter = PMS_NORMAL_SUB_CODE.filter((el) => el.code.includes(grCd));
    return codeFilter[0].type;
  };

  // 본사 일반 - 부서명, 직급명, 관리회사명 코드 조회
  const setPmsNormalSubList = async (subCd: string, setState: Dispatch<SetStateAction<ISelectBox[]>>) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { data, status } = res;
    const cdFind: string = PMS_NORMAL_SUB_CODE.find((el) => el.code.includes(subCd))?.type || '';
    if (status === 200) {
      const defaultObject = { type: cdFind, [cdFind]: '', cdName: t('미선택') };
      const newArr = data.data.pmsNormalTitleList?.map((el: ISelectBox) => ({ type: cdFind, [cdFind]: el.subCd, cdName: el.cdName }));
      setState([defaultObject, ...newArr]);
    }
  };

  const onClickInitiateSearchOption = () => {
    setSearchOptionWorkStatus(INIT_WORKER_STATUS);
    setSearchOption((prev: any) => ({ ...prev, userName: '' }));
    setSearchOptionLockYn({ type: COMCD_LOCK_YN, [COMCD_LOCK_YN]: 'A', cdName: t('전체'), cdSort: 0 });
    setSearchOptionApprovalYn({ type: COMCD_APPROVAL_YN, [COMCD_APPROVAL_YN]: 'A', cdName: t('전체'), cdSort: 0 });
    setSearchOptionUseYn({ type: COMCD_USE_YN, [COMCD_USE_YN]: 'A', cdName: t('전체'), cdSort: 0 });
    setPartCd({ type: subCodeFilter('L'), [subCodeFilter('L')]: '', cdName: t('미선택') });
    setPositionCd({ type: subCodeFilter('M'), [subCodeFilter('M')]: '', cdName: t('미선택') });
    setWWorkstatus(INIT_WORKER_STATUS);
    const tuiForm = initTableState?.map((el: any) => ({
      ...el,
      lockYnCdName: el.lockYn === 'Y' ? t('잠금') : t('미잠금'),
      approvalYnCdName: el.approvalYn === 'Y' ? t('승인') : t('미승인'),
      useYnCdName: el.useYn === 'Y' ? t('사용') : t('미사용'),
    }));
    tuiForm && setInitTableState(tuiForm);
    setIsNewAdd(true);
    setOrgUserInfo(null);
    setSelectedRowKey(null);
    setUJobdate(todayYYYYMMDD());
    setUBdate('');
    setURetiredate('');
    setUserImage('');
    setActiveTabClick(TAB_INFO);
  };

  // 사용자 Row 클릭
  const onClickRow = (rowData: any) => {
    setSelectedRowKey(rowData.rowKey);
    setIsNewAdd(false);

    const user = initTableState?.find((v: any) => v.userId === rowData.userId);
    if (user) {
      setOrgUserInfo({ ...user, postNum: user.zipcode, uAnnualnum: (Number(user.uAnnualnum) / 8).toString(), uRannualnum: (Number(user.uRannualnum) / 8).toString() });

      const splitHpNumber = user.hpNum ? user.hpNum?.split('-') : ['010', '', ''];
      setHpNumState({ num1: splitHpNumber[0] ?? '', num2: splitHpNumber[1] ?? '', num3: splitHpNumber[2] ?? '' });

      const splitTelNumber = user.telNum ? user.telNum?.split('-') : ['', '', ''];
      setTelNumState({ num1: splitTelNumber[0] ?? '', num2: splitTelNumber[1] ?? '', num3: splitTelNumber[2] ?? '' });

      const splitIpNumber = user.ipInfo ? user.ipInfo?.split('.') : ['', '', '', ''];
      if (splitIpNumber.length === 4) setIpInfoState({ num1: splitIpNumber[0] ?? '', num2: splitIpNumber[1] ?? '', num3: splitIpNumber[2] ?? '', num4: splitIpNumber[3] ?? '' });

      const splitEmerNumber = user.emerNum ? user.emerNum?.split('-') : ['', '', ''];
      setEmerNumState({ num1: splitEmerNumber[0] ?? '', num2: splitEmerNumber[1] ?? '', num3: splitEmerNumber[2] ?? '' });

      setPartCd({ type: subCodeFilter('L'), [subCodeFilter('L')]: user.partCd, cdName: partList?.find((el) => el.PART_CD === user.partCd)?.cdName || '' });
      setPositionCd({ type: subCodeFilter('M'), [subCodeFilter('M')]: user.positionCd, cdName: positionList?.find((el) => el.POSITION_CD === user.positionCd)?.cdName || '' });
      setWWorkstatus({ type: 'wWorkstatus', wWorkstatus: user.uWorkstatus, cdName: getWorkStatusName(user.uWorkstatus) });
      setUJobdate(user.uJobdate);
      setUBdate(user.uBdate);
      setURetiredate(user.uRetiredate);
      setUserImage(user.uImg);
    }

    if (size.innerSize.W <= 1024) setViewTable(false);
  };

  const onClickTab = (mrCd: string) => {
    setCurrentTabMrCd(mrCd);
  };

  const onClickSubTab = (tabId: number) => {
    setActiveTabClick(tabId);
  };

  const backToMain = () => {
    setViewTable(true);
    setSelectedRowKey(null);
  };

  const { onBackPrev } = useBackHistory({ isOpen: !viewTable, onPrev: backToMain, pageName: 'cUser1' });

  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const handlFilterSearch = () => {
    setOpenModal({ status: false, type: '' });
  };

  const addBtnAction = () => {
    setIsNewAdd(true);
    setOrgUserInfo(null);
    setSelectedRowKey(null);
    setUseYn(INIT_USE_YN_Y);
    setLockYn(INIT_LOCK_YN_N);
    setActiveTabClick(TAB_INFO);
    setGCd({ type: USER_GRANT as 'USER_GRANT', [USER_GRANT]: '', cdName: '미선택' });
    setPartCd({ type: subCodeFilter('L'), [subCodeFilter('L')]: '', cdName: t('미선택') });
    setPositionCd({ type: subCodeFilter('M'), [subCodeFilter('M')]: '', cdName: t('미선택') });
    setWWorkstatus(INIT_WORKER_STATUS);
    setUJobdate(todayYYYYMMDD());
    setUBdate('');
    setURetiredate('');
    setUserImage('');
    if (size.innerSize.W <= 1024) {
      setViewTable(false);
    }
  };

  const loading = isLoading || !userList;

  // if (userInfo.gCd === 'CU') {
  //   return (
  //     <div className='contents'>
  //       <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
  //       <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
  //         <TuiGridTwoColumnStyle className={size.innerSize.W > 1024 || viewTable ? 'showRoot' : 'hideRoot'}>
  //           <div className='emptyData'>
  //             <img src={illustrator} alt='' />
  //             <p className='main_text text-center'>{t('해당 페이지의 권한이 없습니다 관리자에게 문의해 주시기 바랍니다')}</p>
  //           </div>
  //         </TuiGridTwoColumnStyle>
  //       </div>
  //     </div>
  //   );
  // }

  if (isError) return <IssueGuide />;

  const renderPage = () => {
    if (activeTabClick === TAB_MENU) {
      return <UserMenu isNewAdd={isNewAdd} orgUserInfo={orgUserInfo} viewTableState={initTableState || []} />;
    }
    return (
      <UserInfo
        orgUserInfo={orgUserInfo}
        setOrgUserInfo={setOrgUserInfo}
        applyFilter={applyFilter}
        orgUserList={initTableState}
        setOrgUserList={setInitTableState}
        getUserHeadAPI={refetch}
        lockYnComCdList={lockYnComCdList2}
        useYnComCdList={useYnComCdList2}
        userGrantComCdList={userGrantComCdList}
        isNewAdd={isNewAdd}
        setIsNewAdd={setIsNewAdd}
        setSelectedRowKey={setSelectedRowKey}
        hpNumState={hpNumState}
        telNumState={telNumState}
        ipInfoState={ipInfoState}
        setHpNumState={setHpNumState}
        setTelNumState={setTelNumState}
        setIpInfoState={setIpInfoState}
        emerNumState={emerNumState}
        setEmerNumState={setEmerNumState}
        useYn={useYn}
        lockYn={lockYn}
        setUseYn={setUseYn}
        setLockYn={setLockYn}
        partList={partList}
        positionList={positionList}
        partCd={partCd}
        setPartCd={setPartCd}
        positionCd={positionCd}
        setPositionCd={setPositionCd}
        wWorkstatus={wWorkstatus}
        setWWorkstatus={setWWorkstatus}
        gCd={gCd}
        setGCd={setGCd}
        uJobdate={uJobdate}
        setUJobdate={setUJobdate}
        uBdate={uBdate}
        setUBdate={setUBdate}
        uRetiredate={uRetiredate}
        setURetiredate={setURetiredate}
        userImage={userImage}
        setUserImage={setUserImage}
        addBtnAction={addBtnAction}
        logoType={logoType}
        setLogoType={setLogoType}
        isUpdatedLogo={isUpdatedLogo}
        setIsUpdatedLogo={setIsUpdatedLogo}
      />
    );
  };

  return (
    <div className='contents'>
      <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
      <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
        <Root loading={loading} className={size.innerSize.W > 1024 || viewTable ? 'showRoot' : 'hideRoot'}>
          <SearchOptions align='left'>
            {size.innerSize.W > 1024 ? (
              <>
                <div className='inputsWrapper'>
                  <div className='searchResult'>
                    {t('총')}
                    <span>{viewTableState?.length}</span>
                    {t('개')}
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='wWorkstatus'>{t('근로 상태')}</label>
                      <SelectBox
                        options={WORK_STATUS_SEARCH2}
                        defaultOption={t('재직')}
                        state={searchOptionWorkStatus}
                        setState={setSearchOptionWorkStatus}
                        stateKey='wWorkstatus'
                        initiateKey={searchOptionWorkStatus.wWorkstatus}
                        filterbar='filter-1-left'
                      />
                    </div>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='lockYn'>{t('잠금유무')}</label>
                      <SelectBox
                        options={lockYnComCdList}
                        defaultOption={searchOptionLockYn.cdName}
                        state={searchOptionLockYn}
                        setState={setSearchOptionLockYn}
                        stateKey={COMCD_LOCK_YN}
                        initiateKey={searchOptionLockYn[COMCD_LOCK_YN]}
                        filterbar='filter-1-left'
                      />
                    </div>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='approvalYn'>{t('승인여부')}</label>
                      <SelectBox
                        options={approvalYnComCdList}
                        defaultOption={searchOptionApprovalYn.cdName}
                        state={searchOptionApprovalYn}
                        setState={setSearchOptionApprovalYn}
                        stateKey={COMCD_APPROVAL_YN}
                        initiateKey={searchOptionApprovalYn[COMCD_APPROVAL_YN]}
                        filterbar='filter-1-left'
                      />
                    </div>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='useYn'>{t('사용유무')}</label>
                      <SelectBox
                        options={useYnComCdList}
                        defaultOption={searchOptionUseYn.cdName}
                        state={searchOptionUseYn}
                        setState={setSearchOptionUseYn}
                        stateKey={COMCD_USE_YN}
                        initiateKey={searchOptionUseYn[COMCD_USE_YN]}
                        filterbar='filter-1-center'
                      />
                    </div>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input label='' placeholder={t('사원명')} type='text' id='userName' name='userName' state={searchOption} setState={setSearchOption} />
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                    </div>
                    {userInfo.gCd !== 'CU' && (
                      <div className='flex-basic iconBtnGroup'>
                        <BtnGhost onClick={() => addBtnAction()}>
                          <span className='material-symbols-rounded'>add</span>
                          {t('등록')}
                        </BtnGhost>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <Input label='' placeholder={t('사원명')} type='search' id='userName' name='userName' state={searchOption} setState={setSearchOption} ico='search' />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
                {userInfo.gCd !== 'CU' && (
                  <div className='inputsWrapper'>
                    <div className='flex-basic iconBtnGroup'>
                      <BtnGhost onClick={() => addBtnAction()}>
                        <span className='material-symbols-rounded'>add</span>
                        {t('등록')}
                      </BtnGhost>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={viewTableState}
              columns={columns}
              perPage={15}
              onClickRow={onClickRow}
              rowKey={selectedRowKey}
              frozenCount={frozenCount}
              height={tuiHeight}
              idxKey='not use'
              scrollX={!isMobile()}
            />
          </TuiGridWrapper>
        </Root>
        {(size.innerSize.W > 1024 || !viewTable) && (
          <Root loading={loading}>
            {isNewAdd && userInfo.gCd !== 'CU' ? (
              <div className='inputFormsWrapper flexRowEm'>
                {size.innerSize.W <= 1024 && <BackButton func={onBackPrev} />}
                <div className='formTitle'>{t('새로운 사용자')}</div>
              </div>
            ) : (
              <div className='inputFormsWrapper smallTab'>
                {size.innerSize.W <= 1024 && <BackButton func={onBackPrev} />}
                {tabs
                  .filter((el) => userInfo.gCd !== 'CU')
                  .map((el) => (
                    <div key={el.id} className={`tab ${el.id === activeTabClick && 'activeTab'} userMenu`} role='button' tabIndex={0} onClick={() => onClickSubTab(el.id)}>
                      {t(el.name)}
                    </div>
                  ))}
              </div>
            )}
            {userInfo.gCd !== 'CU' ? renderPage() : isNewAdd ? <IssueGuide type='select' text='사원을 선택하세요' /> : renderPage()}
          </Root>
        )}
      </div>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={handlFilterSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='wWorkstatus'>{t('근로 상태')}</label>
                  <SelectBox
                    options={WORK_STATUS_SEARCH2}
                    defaultOption={t('재직')}
                    state={searchOptionWorkStatus}
                    setState={setSearchOptionWorkStatus}
                    stateKey='wWorkstatus'
                    initiateKey={searchOptionWorkStatus.wWorkstatus}
                    filterbar='filter-1-left'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='lockYn'>{t('잠금유무')}</label>
                  <SelectBox
                    options={lockYnComCdList}
                    defaultOption={searchOptionLockYn.cdName}
                    state={searchOptionLockYn}
                    setState={setSearchOptionLockYn}
                    stateKey={COMCD_LOCK_YN}
                    initiateKey={searchOptionLockYn[COMCD_LOCK_YN]}
                    filterbar='filter-1-left'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='approvalYn'>{t('승인여부')}</label>
                  <SelectBox
                    options={approvalYnComCdList}
                    defaultOption={searchOptionApprovalYn.cdName}
                    state={searchOptionApprovalYn}
                    setState={setSearchOptionApprovalYn}
                    stateKey={COMCD_APPROVAL_YN}
                    initiateKey={searchOptionApprovalYn[COMCD_APPROVAL_YN]}
                    filterbar='filter-1-left'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('사용유무')}</label>
                  <SelectBox
                    options={useYnComCdList}
                    defaultOption={searchOptionUseYn.cdName}
                    state={searchOptionUseYn}
                    setState={setSearchOptionUseYn}
                    stateKey={COMCD_USE_YN}
                    initiateKey={searchOptionUseYn[COMCD_USE_YN]}
                    filterbar='filter-1-center'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default CUser1;
